import { TextField, TextFieldProps } from "@mui/material";
import LibUtils from "lib/LibUtils";
import { useSession } from "providers/SessionProvider";
import React from "react";
import { InputAttributes, NumberFormatValues, NumericFormat, SourceInfo } from "react-number-format";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    defaultValue?: number | null;
    value?: number | null;
    decimals?: number;
    min?: number;
    max?: number;
    allowNegative?: boolean;
    onChange?: (value: number | null, valid: boolean) => void;
    onKeyDown?: InputAttributes['onKeyDown'];
    onMouseUp?: InputAttributes['onMouseUp'];
}

////////////////////////////////////////////////////////////////////////////

export const MprcNumberField: React.FC<IProps & Omit<TextFieldProps, "type" | "onChange" | "autoComplete">> = (props) => {

    const { required = false, allowNegative = false, decimals = 0, min, max, defaultValue, onChange, value, error, disabled, ...rest } = props;
    const { config } = useSession();

    ///
    const validateValue = (value?: number | null) => {
        if (disabled || value === undefined) return true;

        if (error) return !error;

        if (value != null) {
            if (min && value < min) {
                return false;
            }

            if (max && value > max) {
                return false;
            }
        }

        return true;
    };

    return <NumericFormat
        {...rest}
        autoComplete="off"
        aria-autocomplete="none"
        required={required}
        disabled={disabled}
        displayType="input"
        defaultValue={defaultValue}
        value={value ?? (!required ? "" : defaultValue ?? "")}
        allowNegative={allowNegative}
        error={!validateValue(value)}
        onValueChange={
            (values: NumberFormatValues, sourceInfo: SourceInfo) => {
                const isValid = validateValue(values.floatValue);
                if (onChange) onChange(values.floatValue ?? null, isValid);
            }
        }
        customInput={TextField}
        decimalSeparator={decimals > 0 ? LibUtils.getLocaleNumberSeparators(config.defaultLocale).decimalSeparator : undefined}
        decimalScale={decimals > 0 ? decimals : 0}
        onBlur={() => {
            if (!validateValue(value) && onChange) {
                const v = value == null
                    ? defaultValue ?? null
                    : max && value > max
                        ? max
                        : min && value < min
                            ? min
                            : value;
                onChange(v, validateValue(v));
            }
        }}
    />;
};
